import React, { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { ArrowRightIcon, CalendarDaysIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { ButtonLink } from '../../interaxDesignSystemComponents'
import SessionItem, { WeeklySessionData } from '../../interaxDesignSystemComponents/SessionItem'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../constants/colors'
import dayjs from 'dayjs'

interface ThisWeekSessionsProps {
  sessions?: WeeklySessionData[]
  onViewAll?: () => void
  onUpcomingSessions?: () => void
  className?: string
}

export const ThisWeekSessions: React.FC<ThisWeekSessionsProps> = ({
  sessions = [],
  onViewAll,
  onUpcomingSessions,
  className = ''
}) => {
  const { t } = useTranslation()

  // Calculate week range (Monday to Sunday)
  const weekRange = useMemo(() => {
    const now = dayjs()
    const currentDay = now.day() // 0 = Sunday, 1 = Monday, ...
    const daysFromMonday = currentDay === 0 ? 6 : currentDay - 1 // Convert Sunday to 6, rest to days from Monday

    const monday = now.subtract(daysFromMonday, 'day').startOf('day')
    const sunday = monday.add(6, 'day').endOf('day')

    // Format: "Jan 20 - Jan 26, 2025" or "Jan 20 - Feb 2, 2025" if different months
    const mondayFormatted = monday.format('MMM D')
    const sundayFormatted = sunday.format('MMM D, YYYY')

    // If same month, show: "Jan 20 - 26, 2025"
    if (monday.month() === sunday.month() && monday.year() === sunday.year()) {
      return `${mondayFormatted} - ${sunday.date()}, ${sunday.year()}`
    }

    // Different months: "Jan 20 - Feb 2, 2025"
    return `${mondayFormatted} - ${sundayFormatted}`
  }, [])

  return (
    <Box
      className={className}
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '16px',
        boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
        p: 3,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Stack spacing={3} sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack spacing={0.5}>
            <Typography
              component="h2"
              sx={{
                ...INTERAX_TYPOGRAPHY.headline4,
                letterSpacing: '-0.5px',
                color: INTERAX_COLORS.white,
                m: 0
              }}
            >
              {t('facilitator.dashboard.containers.thisWeekSessions.title')}
            </Typography>
            <Typography
              component="p"
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.white70,
                m: 0
              }}
            >
              {weekRange}
            </Typography>
          </Stack>

          <ButtonLink
            onClick={onViewAll}
            iconPosition="right"
            icon={<ArrowRightIcon style={{ width: 16, height: 16, color: INTERAX_COLORS.sun }} />}
          >
            <Typography
              component="span"
              sx={{
                ...INTERAX_TYPOGRAPHY.buttonMedium,
                color: INTERAX_COLORS.sun
              }}
            >
              {t('facilitator.dashboard.containers.thisWeekSessions.viewAll')}
            </Typography>
          </ButtonLink>
        </Stack>

        {sessions.length > 0 ? (
          <Stack spacing={2}>
            {sessions.map((session, index) => (
              <SessionItem key={`${session.title}-${session.day}-${index}`} type="weekly" weeklyData={session} />
            ))}
          </Stack>
        ) : (
          <Stack spacing={2.5} alignItems="center" textAlign="center" sx={{ py: 4 }}>
            <CalendarDaysIcon style={{ width: 64, height: 64, color: INTERAX_COLORS.sun }} />
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body2,
                color: INTERAX_COLORS.white
              }}
            >
              {t('facilitator.dashboard.containers.thisWeekSessions.noActivities')}
            </Typography>

            <ButtonLink
              onClick={onUpcomingSessions}
              iconPosition="right"
              icon={<ArrowRightIcon style={{ width: 16, height: 16, color: INTERAX_COLORS.sun }} />}
            >
              <Typography
                component="span"
                sx={{
                  ...INTERAX_TYPOGRAPHY.buttonMedium,
                  color: INTERAX_COLORS.sun
                }}
              >
                {t('facilitator.dashboard.containers.thisWeekSessions.upcomingSessions')}
              </Typography>
            </ButtonLink>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

export default ThisWeekSessions
